import gql from 'graphql-tag';

import {
  UserDetails,
  UserImages,
  AccountAddressFields,
  AccountAlbumFolderSubscribed,
  AvatarPhoto,
  AccountSearchCreditDetails,
  UserDefaultSettings,
  UserProSettings,
  AccountPhotoManagerSubscriptionDetails,
  AccountPhotoFinderSubscriptionDetails,
  AccountSelected,
  UserTaxDetails,
  AccountReportDetails,
} from '../fragments/account';
import { ActionableItemFields } from '../fragments/actionable-items';
import { AlbumLocationFields, PhotoThumbnails } from '../fragments/album';
import {
  AlbumMembershipsBasic,
  AlbumMembershipsWithAlbum,
  AlbumMembershipsWithAlbumAndPurchasedIdentities,
  AlbumMembershipsWithAlbumAndPurchasedPhotos,
  AlbumMembershipsWithAlbumAndEntitledPhotos,
  AlbumMembershipsWithAlbumAndPhotosTotals,
  AlbumMembershipsWithAlbumAndEntitledPhotosPerIdentity,
  AlbumMembershipsWithAlbumAndMatchedPhotosPerIdentity,
} from '../fragments/album-membership';
import {
  IdentityDetails,
  IdentityDetailsWithPurchasedAlbumFolders,
  IdentityJoinedAlbums,
  IdentityAlbumFolderTagBaseDetails,
  IdentityAlbumFolderUniformTagDetails,
} from '../fragments/identity';
import {
  PrintPackageDetails,
  AlbumPrintPackageDetails,
  PrintPackageForPurchaseDetails,
} from '../fragments/print-packages';
import {
  AlbumPhotoPricingDetails,
  PhotoPricingDetails,
  PhotoPricingForPurchaseDetails,
  PhotoPricingPrintProductConfigurationForPurchaseDetails,
} from '../fragments/photo-pricings';
import { CustomPrintFields } from '../fragments/pro-custom-prints';
import { AutoContributorSettingsDetails } from '../fragments/contributor-assignments';
import { LocationDetails } from '../fragments/location';
import { AlbumFolderTagDetails } from '../fragments/album-folder-tags';
import { TransactionItemDetails } from '../fragments/transaction';
import { albumCustomPrintDetails } from '../fragments/album-custom-print';
import { AlbumCustomPrintForPurchaseDetails } from '../fragments/custom-prints';
import {
  DigitalBundleDetails,
  PurchasedDigitalBundleDetails,
} from '../fragments/digital-bundles';
import { PrintBackgroundDetails } from '../fragments/print-backgrounds';
import { PrintAssetDetails } from '../fragments/print-assets';
import {
  BroadcastDetails,
  BroadcastSequenceDetails,
} from '../fragments/broadcasts';
import { DiscountTemplateDetails } from '../fragments/discount-templates';
import { RosterIdentityBaseDetails } from '../fragments/roster-identity';
import { PhotoForegroundDetails } from '../fragments/photo';

export const MeQuery = gql`
  query meQuery {
    me {
      onboarding: client_store(keys: ["pro_landing", "requested_pro_access"]) {
        data
      }
      upload_consent_notice: client_store(keys: ["upload_consent_notice"]) {
        data
      }
      addresses {
        ...AccountAddressFields
      }
      ...AvatarPhoto
      contact_phone
      roles
      pro_roles
      pro_registration_persona
      email_verified
      active
      organization_name
      web_galleries_enabled
      videos_enabled
      contributor_assignments {
        total
      }
      custom_print_tiers {
        total
      }
      default_consumer_photos_sort_by
      default_consumer_photos_sort_direction
      album_folder_groups_sort

      photo_storage: storage(type: PHOTO) {
        used
        max
      }
      video_storage: storage(type: VIDEO) {
        used
        max
      }
      has_password
      affiliate_signup_url
      affiliate_waldogram_url
      created_at
      site_url
      photo_downloads {
        total
      }
      print_partners {
        total
      }
      linked_identities(relation: PRIMARY, slice: { limit: 1 }) {
        records {
          ...IdentityDetails
        }
      }
      ...UserDetails
      ...UserImages
      ...UserDefaultSettings
      ...UserProSettings
      ...AccountSearchCreditDetails
      ...UserTaxDetails
    }
  }
  ${IdentityDetails}
  ${UserDetails}
  ${UserImages}
  ${UserDefaultSettings}
  ${UserProSettings}
  ${AvatarPhoto}
  ${AccountSearchCreditDetails}
  ${UserTaxDetails}
  ${AccountAddressFields}
`;

export const MeForAlbumPurchase = gql`
  query meForAlbumPurchase($albumId: RecordID!) {
    me {
      ...UserDetails
      ...AlbumMembershipsWithAlbum
      purchased_print_packages(album_id: $albumId, slice: { limit: 999 }) {
        total
        records {
          photo {
            id
          }
          identity {
            id
          }
        }
      }
      purchased_prints {
        purchased_prints_photos(slice: { limit: 999 }) {
          records {
            id
            photo {
              id
            }
          }
        }
      }
    }
  }
  ${UserDetails}
  ${AlbumMembershipsWithAlbum}
`;

export const MeWithPurchasedAlbumPrintsByAlbum = gql`
  query meWithPurchasedAlbumPrintsByAlbum(
    $albumId: RecordID!
    $orderStatuses: [CustomPrintOrderStatusEnum]!
  ) {
    me {
      id
      purchased_custom_prints(
        album_ids: [$albumId]
        order_statuses: $orderStatuses
        custom_print_types: [CUSTOM_PRINT, COLLAGE]
        slice: { limit: 999 }
      ) {
        total
        records {
          id
          name
          photos_no
          selected_identity {
            id
            matched_photos_extended {
              total
            }
          }
        }
      }
    }
  }
`;

export const MeForAlbumPurchaseIdentities = gql`
  query meForAlbumPurchaseIdentities($albumId: RecordID!) {
    me {
      linked_identities(relation: ALL) {
        records {
          ...IdentityDetails
        }
      }
      ...UserDetails
      ...AlbumMembershipsWithAlbumAndPurchasedIdentities
    }
  }
  ${IdentityDetails}
  ${UserDetails}
  ${AlbumMembershipsWithAlbumAndPurchasedIdentities}
`;

export const MeForAlbumPurchasePhotos = gql`
  query meForAlbumPurchasePhotos($albumId: RecordID!) {
    me {
      ...UserDetails
      ...AlbumMembershipsWithAlbumAndPurchasedPhotos
    }
  }
  ${UserDetails}
  ${AlbumMembershipsWithAlbumAndPurchasedPhotos}
`;

export const MeForAlbumEntitledPhotos = gql`
  query meForAlbumEntitledPhotos($albumId: RecordID!) {
    me {
      ...UserDetails
      ...AlbumMembershipsWithAlbumAndEntitledPhotos
    }
  }
  ${UserDetails}
  ${AlbumMembershipsWithAlbumAndEntitledPhotos}
`;

export const MeForAlbumEntitledPhotosPerIdentity = gql`
  query meForAlbumEntitledPhotosPerIdentity($albumId: RecordID!) {
    me {
      ...UserDetails
      ...AlbumMembershipsWithAlbumAndEntitledPhotosPerIdentity
    }
  }
  ${UserDetails}
  ${AlbumMembershipsWithAlbumAndEntitledPhotosPerIdentity}
`;

export const MeForPpdPurchasePerIdentity = gql`
  query MeForPpdPurchasePerIdentity($albumId: RecordID!) {
    me {
      ...UserDetails
      ...AlbumMembershipsWithAlbumAndMatchedPhotosPerIdentity
    }
  }
  ${UserDetails}
  ${AlbumMembershipsWithAlbumAndMatchedPhotosPerIdentity}
`;

export const MeWithIdentities = gql`
  query meWithIdentities {
    me {
      linked_identities(relation: ALL) {
        records {
          ...IdentityDetails
          ...IdentityJoinedAlbums
          ...IdentityDetailsWithPurchasedAlbumFolders
        }
      }
      entitled_matched_photos {
        total
      }
      album_membership_groups {
        total
      }
      all_search_subscriptions: search_subscriptions {
        total
      }
      ...UserDetails
      ...AccountSearchCreditDetails
      addresses {
        ...AccountAddressFields
      }
    }
  }
  ${IdentityDetails}
  ${IdentityJoinedAlbums}
  ${UserDetails}
  ${IdentityDetailsWithPurchasedAlbumFolders}
  ${AccountSearchCreditDetails}
  ${AccountAddressFields}
`;

export const MeWithIdentitiesAndSubscribed = gql`
  query MeWithIdentitiesAndSubscribed($albumFolderIds: [RecordID] = []) {
    me {
      linked_identities(relation: ALL) {
        records {
          ...IdentityDetails
          ...IdentityJoinedAlbums
          ...IdentityDetailsWithPurchasedAlbumFolders
        }
      }
      ...UserDetails
      ...AccountAlbumFolderSubscribed
    }
  }
  ${IdentityDetails}
  ${IdentityJoinedAlbums}
  ${UserDetails}
  ${IdentityDetailsWithPurchasedAlbumFolders}
  ${AccountAlbumFolderSubscribed}
`;

export const MeWithSelectedIdentities = gql`
  query MeWithSelectedIdentities($albumFolderIds: [RecordID] = []) {
    me {
      ...UserDetails
      ...AccountSelected
    }
  }
  ${UserDetails}
  ${AccountSelected}
`;

export const accountUsernameExists = gql`
  query accountUsernameExists($username: String!) {
    accountUsernameExists(username: $username) {
      exists
    }
  }
`;

export const accountPhoneNumberExists = gql`
  query accountPhoneNumberExists($phone_number: PhoneNumber!) {
    accountPhoneNumberExists(phone_number: $phone_number) {
      exists
    }
  }
`;

export const MeWithAlbumMembershipsBasic = gql`
  query meWithAlbumMembershipsBasic {
    me {
      ...UserDetails
      ...AlbumMembershipsBasic
    }
  }
  ${UserDetails}
  ${AlbumMembershipsBasic}
`;

export const MeWithIdentitiesAndAlbumMembershipsTotals = gql`
  query meWithIdentitiesAndAlbumMembershipsTotals {
    me {
      id
      linked_identities(relation: ALL) {
        records {
          ...IdentityDetails
        }
      }
      ...UserDetails
      ...AlbumMembershipsWithAlbumAndPhotosTotals
    }
  }
  ${IdentityDetails}
  ${UserDetails}
  ${AlbumMembershipsWithAlbumAndPhotosTotals}
`;

export const MeWithRoles = gql`
  query meWithRoles {
    me {
      id
      roles
      ...UserDetails
    }
  }
  ${UserDetails}
`;

export const MeForPrintCheckout = gql`
  query meForPrintCheckout {
    me {
      id
      addresses {
        ...AccountAddressFields
      }
    }
  }
  ${AccountAddressFields}
`;

export const AccountBySlug = gql`
  query accountBySlug($slug: String!) {
    accountBySlug(slug: $slug) {
      id
    }
  }
`;

export const MeWithIdentitiesWithTags = gql`
  query MeWithIdentitiesWithTags(
    $albumFolderId: RecordID!
    $albumFolderIds: [RecordID] = []
  ) {
    me {
      linked_identities(relation: ALL) {
        records {
          ...IdentityDetails
          ...IdentityJoinedAlbums
          ...IdentityDetailsWithPurchasedAlbumFolders
          album_folder_tags(album_folder_id: $albumFolderId) {
            records {
              id
              ...IdentityAlbumFolderTagBaseDetails
              ...IdentityAlbumFolderUniformTagDetails
            }
          }
        }
      }
      ...UserDetails
      ...AccountAlbumFolderSubscribed
    }
  }
  ${IdentityDetails}
  ${IdentityJoinedAlbums}
  ${UserDetails}
  ${IdentityDetailsWithPurchasedAlbumFolders}
  ${IdentityAlbumFolderTagBaseDetails}
  ${IdentityAlbumFolderUniformTagDetails}
  ${AccountAlbumFolderSubscribed}
`;

export const MeWithPrintPackages = gql`
  query meWithPrintPackages(
    $pricing_category_ids: [RecordID!]
    $soft_deleted_filter: SoftDeletedFilterEnum
    $print_partner_id: RecordID
  ) {
    me {
      id
      pro_settings {
        default_currency
      }
      print_packages(
        slice: { limit: 9999 }
        pricing_category_ids: $pricing_category_ids
        soft_deleted_filter: $soft_deleted_filter
        print_partner_id: $print_partner_id
      ) {
        total
        records {
          ...PrintPackageDetails
          ...AlbumPrintPackageDetails
        }
      }
    }
  }
  ${PrintPackageDetails}
  ${AlbumPrintPackageDetails}
`;

export const MeWithPhotoPricings = gql`
  query meWithPhotoPricings(
    $pricing_category_ids: [RecordID!]
    $print_partner_id: RecordID
  ) {
    me {
      id
      pro_settings {
        default_currency
      }
      photo_pricings(
        slice: { limit: 9999 }
        pricing_category_ids: $pricing_category_ids
        print_partner_id: $print_partner_id
      ) {
        total
        records {
          ...PhotoPricingDetails
          ...AlbumPhotoPricingDetails
        }
      }
    }
  }
  ${PhotoPricingDetails}
  ${AlbumPhotoPricingDetails}
`;

export const MeWithCustomPrints = gql`
  query meWithCustomPrints(
    $customPrintId: RecordID
    $customPrintTypes: [CustomPrintTypeEnum]
    $customPrintSubType: CustomPrintSubtypeEnum
    $search_query: String
    $pricing_category_ids: [RecordID!]
    $soft_deleted_filter: SoftDeletedFilterEnum
  ) {
    me {
      id
      pro_settings {
        default_currency
        locked_print_partner {
          id
        }
        default_print_partner {
          id
        }
      }
      custom_prints(
        slice: { limit: 9999 }
        custom_print_id: $customPrintId
        custom_print_types: $customPrintTypes
        custom_print_subtype: $customPrintSubType
        search_query: $search_query
        pricing_category_ids: $pricing_category_ids
        soft_deleted_filter: $soft_deleted_filter
      ) {
        total
        records {
          ...CustomPrintFields
        }
      }
    }
  }
  ${CustomPrintFields}
`;

export const MeWithCircles = gql`
  query meWithCircles {
    me {
      id
      circles(slice: { limit: 9999 }) {
        total
        records {
          id
          name
          asset_image {
            id
            url
          }
          contacts {
            total
          }
        }
      }
    }
  }
`;

export const MeWithContacts = gql`
  query meWithContacts(
    $search_query: String
    $email_address: String
    $phone_number: PhoneNumber
  ) {
    me {
      id
      contacts(
        search_query: $search_query
        email_address: $email_address
        phone_number: $phone_number
      ) {
        total
        records {
          id
          first_name
          last_name
          emails(slice: { limit: 1 }) {
            records {
              id
              email_address
            }
          }
          phones(slice: { limit: 1 }) {
            records {
              id
              phone_number
            }
          }
        }
      }
    }
  }
`;

export const MeWithSelectedIdentitiesCustomPrints = gql`
  query meWithSelectedIdentitiesCustomPrints(
    $custom_print_types: [CustomPrintTypeEnum]
    $custom_print_subtypes: [CustomPrintSubtypeEnum]
  ) {
    me {
      id
      album_custom_print_selected_identities(
        custom_print_types: $custom_print_types
        custom_print_subtypes: $custom_print_subtypes
      ) {
        selected_identity {
          id
          identity {
            id
            name
            current_photo {
              ...PhotoThumbnails
            }
            current_matching_photo {
              ...PhotoThumbnails
            }
          }
        }
        album_custom_print {
          ...albumCustomPrintDetails
        }
      }
    }
  }
  ${albumCustomPrintDetails}
  ${PhotoThumbnails}
`;

export const UserAddresses = gql`
  query userAddresses {
    me {
      id
      addresses {
        ...AccountAddressFields
      }
    }
  }
  ${AccountAddressFields}
`;

export const UserAnalyticsFunnelData = gql`
  query userAnalyticsFunnelData($albumFolderId: RecordID!) {
    me {
      id
      name
      album_memberships(with_paid: true, claimed_status_filter: CLAIMED) {
        total
      }
      selected_identities(
        album_folder_ids: [$albumFolderId]
        claimed_status_filter: CLAIMED
        sort_by: { field: SELECTED_IDENTITY_CREATED_AT, direction: ASC }
        slice: { limit: 1 }
      ) {
        records {
          id
          created_at
        }
      }
    }
  }
`;

export const MeWithGalleries = gql`
  query meWithGalleries(
    $albumType: AlbumTypesType
    $albumSubtype: AlbumSubtypesType
  ) {
    me {
      id
      name
      username
      email_address
      album_folders(
        slice: { limit: 999 }
        album_type: $albumType
        album_subtype: $albumSubtype
        root_only: true
      ) {
        records {
          id
          name
          password
          privacy
          invite_only_access
          start_date
          end_date
          is_demo
          shoot_type
          skip_subscription_setup_prompt
          enable_external_upload
          search_subscription_credits_joins_enabled
          org_pay_photo_pricing_enabled
          picture_day_enabled
          photo_sharing_mode
          album_subtype
          album_type
          album_price_mode
          default_album_delivery_mode
          default_matches_only_setting
          is_web_gallery
          joins_enabled
          price {
            currency
            value
          }
          auto_contributor_settings {
            ...AutoContributorSettingsDetails
          }
          location {
            ...AlbumLocationFields
          }
          tags(album_folder_tag_types: [UNIFORM]) {
            total
          }
          green_screen_enabled
          pricing_categories(slice: { limit: 99 }) {
            total
            records {
              id
              pricing_category {
                id
                name
              }
            }
          }
          grades(slice: { limit: 99 }) {
            total
            records {
              id
              grade
              classes {
                total
                records {
                  id
                  class_name
                }
              }
            }
          }
        }
        total
      }
    }
  }
  ${AutoContributorSettingsDetails}
  ${AlbumLocationFields}
`;

export const accountEmailExists = gql`
  query accountEmailExists($email_address: String!) {
    accountEmailExists(email_address: $email_address) {
      exists
      is_verified
    }
  }
`;

export const accountActionableItems = gql`
  query AccountActionableItems($limit: Int = 20, $offset: Int = 0) {
    me {
      id
      actionable_items(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          ...ActionableItemFields
        }
      }
    }
  }
  ${ActionableItemFields}
`;

export const accountWithBaseContent = gql`
  query AccountWithBaseContent(
    $album_subtype: AlbumSubtypesType
    $album_type: AlbumTypesType
  ) {
    me {
      id
      album_folders(album_type: $album_type, album_subtype: $album_subtype) {
        total
      }
      contributor_hierarchy_items(slice: { limit: 1 }, album_type: PRO) {
        total
      }
      has_joined_content
    }
  }
`;

export const AccountForAlbumSearch = gql`
  query accountForAlbumSearch(
    $album_name: String
    $root_album_folder_ids: [RecordID]
    $limit: Int = 5
  ) {
    me {
      id
      albums(
        root_album_folder_ids: $root_album_folder_ids
        album_name: $album_name
        slice: { limit: $limit }
      ) {
        records {
          id
          name

          album_folder {
            id
            name
          }
        }
      }
    }
  }
`;

export const MeForReferrals = gql`
  query meForReferrals {
    me {
      ...UserDetails
      ...UserImages
      created_at
      affiliate_tier_1_enabled
      affiliate_urls(active_status: ACTIVE, slice: { limit: 999 }) {
        records {
          id
          type
          is_active
          url
        }
      }

      promo_codes(active_status: ACTIVE, slice: { limit: 999 }) {
        records {
          id
          type
          is_active
          code
        }
      }
      search_subscription_free_trial_enabled
      search_subscriptions(slice: { limit: 1 }, active_only: true) {
        total
        records {
          id
          trial_days
        }
      }
    }
  }
  ${UserDetails}
  ${UserImages}
`;

export const AccountPhotoSearchCheckout = gql`
  query AccountPhotoSearchCheckout {
    me {
      id
      ...AccountSearchCreditDetails
    }
  }
  ${AccountSearchCreditDetails}
`;

export const AccountPhotoManagerSubscriptions = gql`
  query accountPhotoManagerSubscriptions {
    me {
      id
      name
      roles
      email_address
      pro_registration_persona
      ...AccountPhotoManagerSubscriptionDetails
      ...AccountPhotoFinderSubscriptionDetails
      ...UserProSettings
      storage {
        used
        max
      }
      photo_storage: storage(type: PHOTO) {
        used
        max
      }
      video_storage: storage(type: VIDEO) {
        used
        max
      }
      default_payment_method {
        id
        brand
        last_4
        expiry_month
        expiry_year
      }
    }
  }
  ${AccountPhotoManagerSubscriptionDetails}
  ${AccountPhotoFinderSubscriptionDetails}
  ${UserProSettings}
`;

export const MeForShutterbugSidebar = gql`
  query meForShutterbugSidebar {
    me {
      linked_identities(relation: ALL) {
        records {
          ...IdentityDetails
          ...IdentityJoinedAlbums
          ...IdentityDetailsWithPurchasedAlbumFolders
        }
      }
      shared_identities(slice: { limit: 999 }) {
        records {
          ...IdentityDetails
          entitled_matched_photos {
            total
          }
        }
      }
      entitled_matched_photos {
        total
      }
      album_membership_groups {
        total
      }
      all_search_subscriptions: search_subscriptions {
        total
      }
      has_contributor_assignments(album_type: PRO)
      ...UserDetails
      ...AccountSearchCreditDetails
    }
  }
  ${IdentityDetails}
  ${IdentityJoinedAlbums}
  ${UserDetails}
  ${IdentityDetailsWithPurchasedAlbumFolders}
  ${AccountSearchCreditDetails}
`;

export const MeWithIdentityShares = gql`
  query meWithIdentityShares($status: IdentityShareStatusEnum) {
    me {
      identity_shares(direction: RECEIVED, status: $status) {
        records {
          id
          status
          identity {
            ...IdentityDetails
            ...IdentityJoinedAlbums
            ...IdentityDetailsWithPurchasedAlbumFolders
          }
        }
      }
      shared_identities(slice: { limit: 999 }) {
        records {
          ...IdentityDetails
          ...IdentityJoinedAlbums
          ...IdentityDetailsWithPurchasedAlbumFolders
        }
      }
      has_contributor_assignments(album_type: PRO)
      ...UserDetails
      ...AccountSearchCreditDetails
    }
  }
  ${IdentityDetails}
  ${IdentityJoinedAlbums}
  ${UserDetails}
  ${IdentityDetailsWithPurchasedAlbumFolders}
  ${AccountSearchCreditDetails}
`;

export const MeForMatchedPhotos = gql`
  query meForMatchedPhotos(
    $limit: Int = 100
    $offset: Int = 0
    $identity_ids: [RecordID]
  ) {
    me {
      ...UserDetails
      ...UserImages
      entitled_matched_photos(
        slice: { limit: $limit, offset: $offset }
        identity_ids: $identity_ids
      ) {
        total
        records {
          id
          photo {
            ...PhotoThumbnails
          }
          album {
            id
            album_type
          }
        }
      }
    }
  }
  ${UserDetails}
  ${UserImages}
  ${PhotoThumbnails}
`;

export const MeForIdentityShareMatches = gql`
  query meForIdentityShareMatches(
    $limit: Int = 100
    $offset: Int = 0
    $identity_share_id: RecordID
  ) {
    me {
      ...UserDetails
      ...UserImages
      identity_shares(identity_share_id: $identity_share_id) {
        records {
          identity {
            entitled_matched_photos(slice: { limit: $limit, offset: $offset }) {
              total
              records {
                ...PhotoThumbnails
                album {
                  id
                  album_type
                }
              }
            }
          }
        }
      }
    }
  }
  ${UserDetails}
  ${UserImages}
  ${PhotoThumbnails}
`;

export const identityForEntitledMatchedPhotos = gql`
  query meForIdentityShareMatches(
    $limit: Int = 100
    $offset: Int = 0
    $identity_id: RecordID!
    $album_folder_id: RecordID
    $album_ids: [RecordID]
  ) {
    identity(id: $identity_id) {
      id
      entitled_matched_photos(
        slice: { limit: $limit, offset: $offset }
        album_folder_id: $album_folder_id
        album_ids: $album_ids
      ) {
        total
        records {
          ...PhotoThumbnails
          ...PhotoForegroundDetails
          album {
            id
            album_type
            root_album_folder {
              id
              picture_day_enabled
            }
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${PhotoForegroundDetails}
`;

export const IdentityForConsumerMaybesCheck = gql`
  query identityForConsumerMaybesCheck(
    $identity_id: RecordID!
    $hierarchy_item_id: RecordID
  ) {
    identity(id: $identity_id) {
      id
      name
      has_consumer_maybe_votes(hierarchy_item_id: $hierarchy_item_id)
      current_photo {
        ...PhotoThumbnails
      }
      current_matching_photo {
        ...PhotoThumbnails
      }
    }
  }
  ${PhotoThumbnails}
`;

export const IdentityForConsumerMaybeVotes = gql`
  query identityForConsumerMaybeVotes(
    $identity_id: RecordID!
    $hierarchy_item_id: RecordID
  ) {
    identity(id: $identity_id) {
      id
      name
      has_consumer_maybe_votes(hierarchy_item_id: $hierarchy_item_id)
      current_photo {
        ...PhotoThumbnails
      }
      current_matching_photo {
        ...PhotoThumbnails
      }
      consumer_maybe_votes(
        slice: { limit: 999 }
        hierarchy_item_id: $hierarchy_item_id
      ) {
        total
        records {
          id
          vote
          applied
          compared_face {
            id
            facebox
            photo {
              ...PhotoThumbnails
            }
            detected_race
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AccountForNullStates = gql`
  query accountForNullStates(
    $album_type_subtype_filters: [AlbumTypeSubtypeFilter]
  ) {
    me {
      id
      name
      username
      album_folders(
        root_only: true
        album_type_subtype_filters: $album_type_subtype_filters
      ) {
        total
      }
      albums(
        access: CREATOR
        album_type_subtype_filters: $album_type_subtype_filters
      ) {
        total
      }
      pro_settings {
        has_free_photo_manager
        photo_finder_credits_waived
      }
      entitled_photos {
        total
      }
      search_subscription_free_trial_enabled
      photo_manager_subscription_free_trial_enabled
    }
  }
`;

export const AccountForContentSegregation = gql`
  query accountForContentSegregation {
    me {
      id
      hierarchy_groups(include_matched_photos_only: true) {
        total
      }
    }
  }
`;

export const MeForLocations = gql`
  query meForLocations($limit: Int = 25, $offset: Int = 0) {
    me {
      id
      locations(
        types: PRO_DIRECTORY
        slice: { limit: $limit, offset: $offset }
      ) {
        total
        records {
          id
          radius(distance_unit: MILES) {
            value
            unit
          }
          location {
            id
            ...LocationDetails
          }
        }
      }
    }
  }
  ${LocationDetails}
`;

export const MeWithAlbumMembershipByAlbumId = gql`
  query meWithAlbumMembershipByAlbumId($id: RecordID!) {
    me {
      ...UserDetails
      roles
      pro_roles
      album_memberships(album_id: $id) {
        records {
          id
          has_all_photos_access
          root_album_membership_group {
            id
          }
          album {
            id
          }
        }
      }
    }
  }
  ${UserDetails}
`;

export const MeWithBlockedAccounts = gql`
  query meWithBlockedAccounts($limit: Int, $offset: Int) {
    me {
      id
      blocked_accounts(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          id
          blocked_account_profile {
            id
            name
            roles
            username
            organization_name
            avatar_photo {
              id
              thumbnail_urls {
                url
                size_code
                width
                height
              }
            }
            logo_image {
              id
              url
            }
          }
        }
      }
    }
  }
`;

export const MeForAlbumFoldersSearch = gql`
  query meForAlbumFoldersSearch(
    $name: String
    $album_type: AlbumTypesType
    $album_subtype: AlbumSubtypesType
  ) {
    me {
      id
      album_folders(
        name: $name
        slice: { limit: 25 }
        root_only: true
        album_type: $album_type
        album_subtype: $album_subtype
      ) {
        records {
          id
          name
          join_code
          tag_join_enabled
          tags(slice: { limit: 999 }) {
            records {
              ...AlbumFolderTagDetails
            }
          }
        }
      }
    }
  }

  ${AlbumFolderTagDetails}
`;

export const MeForAlbumFoldersSelect = gql`
  query meForAlbumFoldersSelect(
    $album_type_subtype_web_app_basename_filters: [AlbumTypeSubtypeWebAppBasenameFilter]
  ) {
    me {
      id
      album_folders(
        slice: { limit: 99 }
        root_only: true
        album_type_subtype_web_app_basename_filters: $album_type_subtype_web_app_basename_filters
      ) {
        total
        records {
          id
          name
        }
      }
    }
  }
`;

export const MeWithPurchasedItems = gql`
  query meWithPurchasedItems(
    $album_folder_id: RecordID!
    $print_packages_status: PurchasedPrintPackageStatusEnum
    $photo_pricings_status: PurchasedPhotoPricingStatusEnum
    $custom_prints_statuses: [CustomPrintOrderStatusEnum]
    $is_prepaid: Boolean
  ) {
    me {
      id
      total_purchased_print_packages: purchased_print_packages(
        album_folder_id: $album_folder_id
      ) {
        total
      }
      purchased_print_packages(
        album_folder_id: $album_folder_id
        status: $print_packages_status
        is_prepaid: $is_prepaid
      ) {
        total
        records {
          id
          identity {
            id
            name
            last_name
          }
          print_package {
            ...PrintPackageForPurchaseDetails
          }
          is_prepaid
          quantity
          transaction {
            id
          }
        }
      }
      purchased_photo_pricings(
        album_folder_id: $album_folder_id
        status: $photo_pricings_status
        is_prepaid: $is_prepaid
      ) {
        total
        records {
          id
          photo_pricing {
            ...PhotoPricingForPurchaseDetails
          }
          photo_pricing_print_item {
            id
            price {
              value
              currency
            }
            type {
              id
              name
              min_price {
                value
                currency
              }
            }
          }
          photo_pricing_print_product_item {
            ...PhotoPricingPrintProductConfigurationForPurchaseDetails
          }
          is_prepaid
          quantity
          transaction {
            id
          }
        }
      }
      purchased_custom_prints(
        album_folder_id: $album_folder_id
        order_statuses: $custom_prints_statuses
        is_prepaid: $is_prepaid
      ) {
        total
        records {
          id
          quantity
          order_status
          album_custom_print {
            ...AlbumCustomPrintForPurchaseDetails
          }
          identity {
            id
            name
            last_name
          }
          transaction {
            id
          }
        }
      }
    }
  }
  ${PrintPackageForPurchaseDetails}
  ${PhotoPricingForPurchaseDetails}
  ${PhotoPricingPrintProductConfigurationForPurchaseDetails}
  ${AlbumCustomPrintForPurchaseDetails}
`;

export const MeWithPurchasedDigitalBundles = gql`
  query meWithPurchasedDigitalBundles(
    $identity_id: RecordID
    $album_folder_id: RecordID
    $digital_bundle_status: PurchasedDigitalBundleStatusEnum
    $pending_configuration_only: Boolean
  ) {
    me {
      id
      purchased_digital_bundles(
        identity_id: $identity_id
        album_folder_id: $album_folder_id
        status: $digital_bundle_status
        pending_configuration_only: $pending_configuration_only
      ) {
        total
        records {
          ...PurchasedDigitalBundleDetails
        }
      }
    }
  }
  ${PurchasedDigitalBundleDetails}
`;

export const AccountForRevenue = gql`
  query accountForRevenue(
    $limit: Int = 25
    $offset: Int = 0
    $album_id: RecordID
    $album_folder_id: RecordID
    $album_folder_ids: [RecordID]
    $album_ids: [RecordID]
    $date_period: DatePeriodEnum
    $date_range: DateFilterInput
    $account_id: RecordID
    $has_contributor_account: Boolean
    $contributor_account_id: RecordID
    $contributor_account_ids: [RecordID!]
    $order_number_search: String
    $account_search: String
  ) {
    me {
      id
      print_partners {
        total
        records {
          id
          print_partner {
            id
          }
          print_partner_account_id
        }
      }
      vendor_transaction_items(
        slice: { limit: $limit, offset: $offset }
        include_album_folder_albums: true
        album_folder_ids: $album_folder_ids
        album_ids: $album_ids
        date_range: $date_range
        account_id: $account_id
        has_contributor_account: $has_contributor_account
        contributor_account_id: $contributor_account_id
        order_number_search: $order_number_search
        account_search: $account_search
      ) {
        total
        records {
          ...TransactionItemDetails
        }
      }
      transaction_items_revenue(
        album_id: $album_id
        album_folder_id: $album_folder_id
        include_album_folder_albums: true
        date_period: $date_period
        date_range: $date_range
        has_contributor_account: $has_contributor_account
        contributor_account_ids: $contributor_account_ids
      ) {
        gross {
          value
          currency
        }
        print_cost {
          value
          currency
        }
        shipping_cost {
          value
          currency
        }
        stripe_fee {
          value
          currency
        }
        waldo_fee {
          value
          currency
        }
        refunds {
          value
          currency
        }
        tax {
          value
          currency
        }
        vendor_tax {
          value
          currency
        }
        net {
          value
          currency
        }
        transactions_count
        avg_net_per_transaction
      }
      transaction_items_contributors_attributed_price_sum
    }
  }
  ${TransactionItemDetails}
`;

export const AccountSalesSummary = gql`
  query accountSalesSummary {
    me {
      id
      current_day_sales: transaction_items_revenue(date_period: CURRENT_DAY) {
        net {
          value
          currency
        }
      }
      current_week_sales: transaction_items_revenue(date_period: CURRENT_WEEK) {
        net {
          value
          currency
        }
      }
      current_month_sales: transaction_items_revenue(
        date_period: CURRENT_MONTH
      ) {
        net {
          value
          currency
        }
      }
      current_year_sales: transaction_items_revenue(date_period: CURRENT_YEAR) {
        net {
          value
          currency
        }
      }
      transaction_items_revenue {
        gross {
          value
          currency
        }
        print_cost {
          value
          currency
        }
        shipping_cost {
          value
          currency
        }
        stripe_fee {
          value
          currency
        }
        waldo_fee {
          value
          currency
        }
        refunds {
          value
          currency
        }
        tax {
          value
          currency
        }
        vendor_tax {
          value
          currency
        }
        net {
          value
          currency
        }
        transactions_count
        avg_net_per_transaction
      }
    }
  }
`;

export const AccountEventsSummary = gql`
  query accountEventsSummary {
    me {
      id
      revenue_hierarchy_items(slice: { limit: 7 }) {
        records {
          __typename
          ... on RevenueHierarchyAlbumFolderItem {
            id
            album_folder {
              id
              name
            }
            latest_transaction_item_created_at
          }
          ... on RevenueHierarchyAlbumItem {
            id
            album {
              id
              name
            }
            latest_transaction_item_created_at
          }
        }
      }
    }
  }
`;

export const AccountTransactionsSummary = gql`
  query accountTransactionsSummary {
    me {
      id
      vendor_transaction_items(slice: { limit: 7 }) {
        records {
          __typename
          ... on ITransactionItem {
            id
            account {
              id
              name
              username
              email_address
            }
            price {
              value
              currency
            }
          }
        }
      }
    }
  }
`;

export const AccountForPurchasedEventAddons = gql`
  query accountForPurchasedEventAddons {
    me {
      id
      purchased_event_addons(unassigned_album_folder_only: true) {
        records {
          id
          event_addon_type
          transaction {
            id
          }
          purchased_event {
            id
          }
        }
      }
    }
  }
`;

export const AccountForConsumerMaybesCheck = gql`
  query accountForConsumerMaybesCheck($hierarchy_item_id: RecordID) {
    me {
      id
      has_consumer_maybe_votes(hierarchy_item_id: $hierarchy_item_id)
    }
  }
`;

export const AccountForConsumerMaybesIdentities = gql`
  query accountForConsumerMaybesIdentities($hierarchy_item_id: RecordID) {
    me {
      id
      name
      has_consumer_maybe_votes(hierarchy_item_id: $hierarchy_item_id)
      identities_with_consumer_maybe_votes(
        slice: { limit: 30 }
        hierarchy_item_id: $hierarchy_item_id
      ) {
        total
        records {
          id
          current_photo {
            ...PhotoThumbnails
          }
          current_matching_photo {
            ...PhotoThumbnails
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const MeForPurchasedEventAddonsList = gql`
  query MeForPurchasedEventAddonsList($limit: Int = 999, $offset: Int = 0) {
    me {
      id
      purchased_event_addons(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          id
          album {
            id
            album_folder {
              id
              name
              album_type
              album_subtype
              purchased_event_addon_types
              storage {
                max
                used
              }
              created_at
            }
            soft_deleted
          }
          album_folder {
            id
            name
            album_type
            album_subtype
            purchased_event_addon_types
            storage {
              max
              used
            }
            created_at
          }
          transaction {
            id
            total
            transaction_items {
              total
              records {
                ... on TransactionEventAddonItem {
                  id
                  item_type
                  purchased_event_addon {
                    id
                    event_addon_type
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const clientStoreGet = gql`
  query clientStoreGet($accountId: RecordID!, $keys: [String]) {
    clientStoreGet(account_id: $accountId, keys: $keys) {
      data
    }
  }
`;

export const MeForCheckout = gql`
  query meForCheckout {
    me {
      id
      default_payment_method {
        id
        brand
        last_4
        expiry_month
        expiry_year
      }
      payment_methods {
        id
        brand
        last_4
        expiry_month
        expiry_year
      }
    }
  }
`;

export const MeForPrintPartners = gql`
  query meForPrintPartners {
    me {
      id
      ...UserDetails
      ...UserProSettings
      print_partners(slice: { limit: 999 }) {
        records {
          id
          status
          print_partner_account_id
          shipping_price {
            value
            currency
          }
          print_partner {
            id
            name
            currency
          }
        }
      }
    }
  }
  ${UserDetails}
  ${UserProSettings}
`;

export const MeWithPrintBackgrounds = gql`
  query meWithPrintBackgrounds(
    $search_query: String
    $pricing_category_ids: [RecordID!]
  ) {
    me {
      id
      print_backgrounds(
        search_query: $search_query
        pricing_category_ids: $pricing_category_ids
        slice: { limit: 9999 }
      ) {
        total
        records {
          ...PrintBackgroundDetails
        }
      }
    }
  }
  ${PrintBackgroundDetails}
`;

export const MeWithPrintAssets = gql`
  query meWithPrintAssets(
    $search_query: String
    $pricing_category_ids: [RecordID!]
    $print_asset_type: PrintAssetTypeEnum
  ) {
    me {
      id
      print_assets_total: print_assets(print_asset_type: $print_asset_type) {
        total
      }
      print_assets(
        search_query: $search_query
        pricing_category_ids: $pricing_category_ids
        print_asset_type: $print_asset_type
        slice: { limit: 9999 }
      ) {
        total
        records {
          ...PrintAssetDetails
        }
      }
    }
  }
  ${PrintAssetDetails}
`;

export const MeWithBroadcastSequences = gql`
  query meWithBroadcastSequences(
    $broadcast_sequence_id: RecordID
    $search_query: String
  ) {
    me {
      id
      broadcast_sequences(
        slice: { limit: 999 }
        broadcast_sequence_id: $broadcast_sequence_id
        search_query: $search_query
      ) {
        total
        records {
          ...BroadcastSequenceDetails
        }
      }
    }
  }
  ${BroadcastSequenceDetails}
`;

export const MeWithBroadcasts = gql`
  query MeWithBroadcasts(
    $album_folder_id: RecordID
    $limit: Int = 50
    $offset: Int = 0
  ) {
    me {
      id
      broadcasts(
        slice: { limit: $limit, offset: $offset }
        album_folder_id: $album_folder_id
      ) {
        total
        records {
          ...BroadcastDetails
        }
      }
    }
  }
  ${BroadcastDetails}
`;

export const MeWithDiscountTemplates = gql`
  query meWithDiscountTemplates($discount_template_id: RecordID) {
    me {
      id
      discount_templates(
        slice: { limit: 999 }
        discount_template_id: $discount_template_id
      ) {
        total
        records {
          ...DiscountTemplateDetails
        }
      }
    }
  }
  ${DiscountTemplateDetails}
`;

export const MeWithRosterIdentity = gql`
  query MeWithRosterIdentity($roster_identity_id: RecordID) {
    me {
      id
      roster_identities(roster_identity_ids: [$roster_identity_id]) {
        total
        records {
          ...RosterIdentityBaseDetails
        }
      }
    }
  }
  ${RosterIdentityBaseDetails}
`;

export const MeWithAlbumFolderRosterIdentitiesTotal = gql`
  query MeWithAlbumFolderRosterIdentitiesTotal($id: RecordID!) {
    me {
      id
      roster_identities(album_folder_ids: [$id]) {
        total
      }
    }
  }
`;

export const MeWithAlbumRosterIdentitiesTotal = gql`
  query MeWithAlbumRosterIdentitiesTotal($id: RecordID!) {
    me {
      id
      roster_identities(album_ids: [$id]) {
        total
      }
    }
  }
`;

export const MeWithDigitalBundles = gql`
  query MeWithDigitalBundles($without_hierarchy_item_only: Boolean) {
    me {
      id
      digital_bundles(
        slice: { limit: 99 }
        without_hierarchy_item_only: $without_hierarchy_item_only
      ) {
        total
        records {
          ...DigitalBundleDetails
        }
      }
    }
  }
  ${DigitalBundleDetails}
`;

export const MeWithRootAlbumFolderAlbums = gql`
  query MeWithRootAlbumFolderAlbums($id: RecordID!) {
    me {
      id
      albums(root_album_folder_ids: [$id]) {
        total
      }
    }
  }
`;

export const MeWithAlbumsByShootType = gql`
  query MeWithAlbumsByShootType(
    $id: RecordID!
    $shoot_type: HierarchyShootTypeEnum
  ) {
    me {
      id
      albums(
        root_album_folder_ids: [$id]
        shoot_type: $shoot_type
        slice: { limit: 999 }
      ) {
        total
        records {
          id
        }
      }
    }
  }
`;

export const MeForContributors = gql`
  query MeForContributors($offset: Int = 0, $limit: Int = 999) {
    me {
      id
      transaction_items_contributors_accounts(
        slice: { offset: $offset, limit: $limit }
      ) {
        total
        records {
          id
          name
        }
      }
    }
  }
`;

export const MeWithReports = gql`
  query MeWithReports($album_folder_id: RecordID) {
    me {
      id
      reports(album_folder_id: $album_folder_id) {
        total
        records {
          ...AccountReportDetails
        }
      }
    }
  }
  ${AccountReportDetails}
`;
